import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RefreshCw, Eye } from "lucide-react";
import { Link } from "wouter";
import { format } from "date-fns";
import type { ClientPlan, Client, Plan } from "@shared/schema";
import { calendarDaysUntil, resolvePlanRenewalDate } from "@shared/utils/renewals";
import { usePermissions } from "@/hooks/usePermissions";

type EnrichedClientPlan = ClientPlan & {
  plan?: Partial<Plan> | null;
  totalPremium: string | number;
  status?: string | null;
};

type UpcomingRenewalSummary = {
  clientId: number;
  clientName: string;
  earliestRenewalDate: Date;
  renewalMonthLabel: string;
  planTypes: string[];
};

export function UpcomingRenewals() {
  const { can, isLoading: permissionsLoading } = usePermissions();
  const canViewClients = can("clients:view");
  const canViewPlans = can("plans:view");
  // Need both permissions to show renewals (clients data + plans data)
  const hasPermission = canViewClients && canViewPlans;

  const { data: clientPlans = [], isLoading: clientPlansLoading } = useQuery<ClientPlan[] | EnrichedClientPlan[]>({
    queryKey: ["/api/clientplans"],
    enabled: canViewClients,
  });

  const { data: clients = [], isLoading: clientsLoading } = useQuery<Client[]>({
    queryKey: ["/api/clients"],
    enabled: canViewClients,
  });

  const { data: plans = [], isLoading: plansLoading } = useQuery<Plan[]>({
    queryKey: ["/api/plans"],
    enabled: canViewPlans,
  });

  // Don't render if user doesn't have permission for both
  if (!permissionsLoading && !hasPermission) {
    return null;
  }

  const isLoading = clientPlansLoading || clientsLoading || plansLoading;

  const enrichedPlans: EnrichedClientPlan[] = Array.isArray(clientPlans)
    ? (clientPlans as EnrichedClientPlan[])
    : [];

  // Ensure plans is always an array
  const plansArray = Array.isArray(plans) ? plans : [];

  const getClientName = (clientId: number) => {
    const client = clients.find(entry => entry.id === clientId);
    return client?.name || "Unknown Client";
  };

  const locatePlanById = (planId: number) => {
    return plansArray.find(planEntry => planEntry.id === planId) || null;
  };

  // Date-only resolution shared with the "Renewals (90 days)" card (DEV-326)
  const resolveRenewalDate = (plan: EnrichedClientPlan) =>
    resolvePlanRenewalDate(plan, locatePlanById);

  const isWithinNextNinetyDays = (date: Date, today: Date) => {
    const days = calendarDaysUntil(date, today);
    return days >= 0 && days <= 90;
  };

  // date-fns, not toLocaleString — the latter follows the browser locale
  const formatRenewalMonth = (date: Date) => {
    return format(date, "MMMM yyyy");
  };

  const buildUpcomingRenewalSummaries = () => {
    const today = new Date();

    const byClient = new Map<number, { date: Date; types: Set<string> }>();
    enrichedPlans.forEach(clientPlan => {
      const renewalDate = resolveRenewalDate(clientPlan);
      if (!renewalDate) {
        return;
      }
      if (!isWithinNextNinetyDays(renewalDate, today)) {
        return;
      }

      const libraryPlan = locatePlanById(clientPlan.planId);
      const planType = String(clientPlan.plan?.type || libraryPlan?.type || "");
      const entry = byClient.get(clientPlan.clientId);
      if (!entry) {
        byClient.set(clientPlan.clientId, { date: renewalDate, types: new Set(planType ? [planType] : []) });
      } else {
        if (renewalDate < entry.date) entry.date = renewalDate;
        if (planType) entry.types.add(planType);
      }
    });

    const summaries: UpcomingRenewalSummary[] = Array.from(byClient.entries()).map(
      ([clientId, { date, types }]) => ({
        clientId,
        clientName: getClientName(clientId),
        earliestRenewalDate: date,
        renewalMonthLabel: formatRenewalMonth(date),
        planTypes: Array.from(types),
      })
    );

    return summaries
      .sort(
        (firstSummary, secondSummary) =>
          firstSummary.earliestRenewalDate.getTime() - secondSummary.earliestRenewalDate.getTime()
      )
      .slice(0, 5);
  };

  const renderEmptyState = () => {
    return (
      <div className="text-center py-6">
        <RefreshCw className="h-8 w-8 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-500 mb-4">No upcoming renewals</p>
        <Link href="/renewals">
          <Button variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Renewal Management
          </Button>
        </Link>
      </div>
    );
  };

  const renderRenewalRow = (summary: UpcomingRenewalSummary) => {
    return (
      <div
        key={summary.clientId}
        className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{summary.clientName}</p>
          <p className="text-xs text-gray-500 mt-1 capitalize">
            {summary.planTypes.length > 0 ? summary.planTypes.join(" · ") : "Renewal month"}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge className="text-xs px-3 py-1">{summary.renewalMonthLabel}</Badge>
          <Link href={`/clients/${summary.clientId}`}>
            <Button variant="ghost" size="sm">
              <Eye className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  };

  const renderRenewalList = (summaries: UpcomingRenewalSummary[]) => {
    const renewalRows = summaries.map(summary => renderRenewalRow(summary));

    return (
      <div className="space-y-3">
        {renewalRows}
        <div className="pt-2 border-t">
          <Link href="/renewals">
            <Button variant="ghost" size="sm" className="w-full">
              View Renewal Management
            </Button>
          </Link>
        </div>
      </div>
    );
  };

  const buildCardContent = (summaries: UpcomingRenewalSummary[]) => {
    if (summaries.length === 0) {
      return renderEmptyState();
    }

    return renderRenewalList(summaries);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg font-medium">Upcoming Renewals</CardTitle>
          <RefreshCw className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const upcomingRenewalSummaries = buildUpcomingRenewalSummaries();
  const cardContent = buildCardContent(upcomingRenewalSummaries);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg font-medium">Upcoming Renewals</CardTitle>
        <RefreshCw className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>{cardContent}</CardContent>
    </Card>
  );
}
